import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, ExternalLink } from 'lucide-react';
import { brandInfo, contactInfo } from '../data/bikes';
import { useLanguage } from '../i18n';
import { useTheme } from '../context/ThemeContext';

const MainFooter = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  
  const categories = [
    { id: 'fatbike' },
    { id: 'mtb' },
    { id: 'urban' },
    { id: 'folding' }
  ];

  const handleCategoryClick = (e, categoryId) => {
    const hash = `#category-${categoryId}`;

    // If we're already on home, smooth-scroll to the section
    if (isHomePage) {
      e.preventDefault();
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // From other pages: go home, then scroll to section
    e.preventDefault();
    navigate('/');
    window.setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  };

  return (
    <footer className={`border-t ${isDark ? 'bg-[#0a0a0f] border-[#2a2a3a]' : 'bg-slate-900 border-slate-800'}`} data-testid="main-footer">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-6">
              <span className={`inline-flex items-center justify-center ${isDark ? 'bg-white rounded-full p-2' : ''}`}>
                <img 
                  src={brandInfo.logo} 
                  alt={brandInfo.name}
                  className={`${isDark ? 'h-8' : 'h-10'} drop-shadow-[0_0_10px_rgba(181,161,106,0.35)]`}
                />
              </span>
              <span className="text-white font-bold text-xl">{brandInfo.name}</span>
            </Link>
            <p className="text-white/60 text-sm mb-6 leading-relaxed">
              {t('footer.tagline')}
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3">
              <a 
                href={contactInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:text-[#B5A16A] hover:bg-[#B5A16A]/20 transition-colors border border-white/10"
                aria-label="Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a 
                href={contactInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:text-[#B5A16A] hover:bg-[#B5A16A]/20 transition-colors border border-white/10"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a 
                href={contactInfo.social.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:text-[#B5A16A] hover:bg-[#B5A16A]/20 transition-colors border border-white/10"
                aria-label="YouTube"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a 
                href={contactInfo.social.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:text-[#B5A16A] hover:bg-[#B5A16A]/20 transition-colors border border-white/10"
                aria-label="TikTok"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
              </a>
            </div>
          </div>

          {/* Categories Column */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">{t('footer.categories')}</h3>
            <ul className="space-y-3">
              {categories.map((category) => (
                <li key={category.id}>
                  <a
                    href={isHomePage ? `#category-${category.id}` : `/#category-${category.id}`}
                    onClick={(e) => handleCategoryClick(e, category.id)}
                    className="text-white/60 hover:text-[#B5A16A] transition-colors text-sm"
                  >
                    {t(`categories.${category.id}.name`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">{t('footer.usefulLinks')}</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://www.freewheel.store/products/Accessori-c194466601"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-[#B5A16A] transition-colors text-sm flex items-center gap-1"
                >
                  Accessori
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <a 
                  href={contactInfo.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-[#B5A16A] transition-colors text-sm flex items-center gap-1"
                >
                  {t('footer.store')}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <a 
                  href={contactInfo.appointmentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-[#B5A16A] transition-colors text-sm flex items-center gap-1"
                >
                  {t('footer.bookAppointment')}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">{t('footer.contacts')}</h3>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`https://maps.google.com/?q=${encodeURIComponent(contactInfo.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-white/60 hover:text-[#B5A16A] transition-colors text-sm"
                >
                  <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{contactInfo.address}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`tel:${contactInfo.phone}`}
                  className="flex items-center gap-3 text-white/60 hover:text-[#B5A16A] transition-colors text-sm"
                >
                  <Phone className="w-5 h-5 flex-shrink-0" />
                  <span>{contactInfo.phone}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center gap-3 text-white/60 hover:text-[#B5A16A] transition-colors text-sm"
                >
                  <Mail className="w-5 h-5 flex-shrink-0" />
                  <span>{contactInfo.email}</span>
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/60 text-sm">
                <Clock className="w-5 h-5 flex-shrink-0" />
                <span>{contactInfo.hours}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">
              © {currentYear} {brandInfo.name}. {t('footer.allRightsReserved')}.
            </p>
            <p className="text-white/40 text-sm">
              {t('footer.distributedBy')}{' '}
              <a 
                href={contactInfo.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#B5A16A] hover:underline"
              >
                {contactInfo.shopName}
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default MainFooter;
